import { useState } from 'react'
import * as React from 'react'
import { X, Plus, AlertTriangle, Utensils } from '@/ui/icons'
import { Button } from '@/ui/components'
import { Input } from '@/ui/components'
import { Textarea } from '@/ui/components'
import { Label } from '@/ui/components'
import { UserProfile, UserProfileApi, UpdateUserProfileDto } from '@/shared/api/users'
import { DietaryRestriction, getDietaryRestrictionLabel, DietaryRestrictionType } from '@/shared/types'
import { useAuth } from '@/modules/auth'
import { AuthApi } from '@/shared/api/auth'
import { useSetUser } from '@/stores/authStore'
import { LocationSearchInput, type LocationSearchResult } from '@/modules/trips/components/forms/LocationSearchInput'

interface UserProfileEditModalProps {
  isOpen: boolean
  onClose: () => void
  profile: UserProfile
  onProfileUpdated: (profile: UserProfile) => void
}

// Curated diet-type options for the picklist (allergies are entered free-form)
const DIET_OPTIONS: DietaryRestrictionType[] = [
  DietaryRestriction.VEGETARIAN,
  DietaryRestriction.VEGAN,
  DietaryRestriction.GLUTEN_FREE,
  DietaryRestriction.DAIRY_FREE,
  DietaryRestriction.KOSHER,
  DietaryRestriction.HALAL,
  DietaryRestriction.NO_PORK,
  DietaryRestriction.NO_BEEF,
  DietaryRestriction.LOW_SODIUM,
  DietaryRestriction.DIABETIC_FRIENDLY,
  DietaryRestriction.OTHER
]

// Helper functions to extract username from URL and build URL from username
const extractUsername = (url: string, platform: 'instagram' | 'youtube' | 'tiktok' | 'facebook'): string => {
  if (!url) return ''
  
  try {
    const urlObj = new URL(url.startsWith('http') ? url : `https://${url}`)
    const path = urlObj.pathname.replace(/^\//, '').replace(/\/$/, '')
    
    switch (platform) {
      case 'instagram':
        // Handle instagram.com/username or @username
        if (path.startsWith('@')) return path.substring(1)
        return path.split('/')[0] || ''
      case 'youtube':
        // Handle youtube.com/@username, youtube.com/c/channel, youtube.com/user/username, or youtube.com/channel/ID
        if (path.startsWith('@')) return path.substring(1)
        if (path.startsWith('c/')) return path.substring(2)
        if (path.startsWith('user/')) return path.substring(5)
        if (path.startsWith('channel/')) return path.substring(8)
        return path || ''
      case 'tiktok':
        // Handle tiktok.com/@username or @username
        if (path.startsWith('@')) return path.substring(1)
        return path.split('/')[0] || ''
      case 'facebook':
        // Handle facebook.com/username
        return path.split('/')[0] || ''
      default:
        return ''
    }
  } catch {
    // If URL parsing fails, try to extract username from common patterns
    const match = url.match(/(?:@|instagram\.com\/|youtube\.com\/@?|tiktok\.com\/@?|facebook\.com\/)([^\/\s]+)/)
    return match ? match[1] : url
  }
}

const buildSocialUrl = (username: string, platform: 'instagram' | 'youtube' | 'tiktok' | 'facebook'): string => {
  if (!username || !username.trim()) return ''
  
  const cleanUsername = username.trim().replace(/^@/, '').replace(/^https?:\/\//, '').split('/')[0]
  
  switch (platform) {
    case 'instagram':
      return `https://instagram.com/${cleanUsername}`
    case 'youtube':
      // Default to @ format for YouTube
      return `https://youtube.com/@${cleanUsername}`
    case 'tiktok':
      return `https://tiktok.com/@${cleanUsername}`
    case 'facebook':
      return `https://facebook.com/${cleanUsername}`
    default:
      return ''
  }
}

export function UserProfileEditModal({ isOpen, onClose, profile, onProfileUpdated }: UserProfileEditModalProps) {
  const { getAccessToken, user } = useAuth()
  const setUser = useSetUser()
  
  // Initialize form with usernames extracted from URLs and user metadata
  const [formData, setFormData] = useState({
    full_name: user?.user_metadata?.full_name || '',
    bio: profile.bio || '',
    location: profile.location || '',
    years_overlanding: profile.years_overlanding || 0,
    instagram_username: extractUsername(profile.instagram_url || '', 'instagram'),
    youtube_username: extractUsername(profile.youtube_url || '', 'youtube'),
    tiktok_username: extractUsername(profile.tiktok_url || '', 'tiktok'),
    facebook_username: extractUsername(profile.facebook_url || '', 'facebook'),
    website_url: profile.website_url || ''
  })

  const [allergies, setAllergies] = useState<string[]>(profile.allergies || [])
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>(profile.dietary_restrictions || [])
  const [allergyInput, setAllergyInput] = useState('')

  // Home base — powers weekly weekend-trip suggestions (feed prompts for this too,
  // but it can also be set/changed here directly).
  const [homeBaseText, setHomeBaseText] = useState(profile.home_location_label || '')
  const [homeCoords, setHomeCoords] = useState<{ lat: number; lng: number } | null>(
    profile.home_latitude != null && profile.home_longitude != null
      ? { lat: profile.home_latitude, lng: profile.home_longitude }
      : null
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const userProfileApi = new UserProfileApi(getAccessToken)
  const authApi = new AuthApi(getAccessToken)

  // Update form data when profile or user changes
  React.useEffect(() => {
    if (isOpen) {
      setFormData({
        full_name: user?.user_metadata?.full_name || '',
        bio: profile.bio || '',
        location: profile.location || '',
        years_overlanding: profile.years_overlanding || 0,
        instagram_username: extractUsername(profile.instagram_url || '', 'instagram'),
        youtube_username: extractUsername(profile.youtube_url || '', 'youtube'),
        tiktok_username: extractUsername(profile.tiktok_url || '', 'tiktok'),
        facebook_username: extractUsername(profile.facebook_url || '', 'facebook'),
        website_url: profile.website_url || ''
      })
      setAllergies(profile.allergies || [])
      setDietaryRestrictions(profile.dietary_restrictions || [])
      setAllergyInput('')
      setHomeBaseText(profile.home_location_label || '')
      setHomeCoords(
        profile.home_latitude != null && profile.home_longitude != null
          ? { lat: profile.home_latitude, lng: profile.home_longitude }
          : null
      )
    }
  }, [profile, user, isOpen])

  const handleHomeBaseSelect = (result: LocationSearchResult) => {
    setHomeBaseText(result.address || result.name)
    setHomeCoords({ lat: result.latitude, lng: result.longitude })
  }

  const handleAddAllergy = () => {
    const trimmed = allergyInput.trim()
    if (!trimmed) return
    if (!allergies.some(a => a.toLowerCase() === trimmed.toLowerCase())) {
      setAllergies([...allergies, trimmed])
    }
    setAllergyInput('')
  }

  const handleRemoveAllergy = (index: number) => {
    setAllergies(allergies.filter((_, i) => i !== index))
  }

  const toggleDiet = (code: DietaryRestrictionType) => {
    setDietaryRestrictions(prev =>
      prev.includes(code) ? prev.filter(d => d !== code) : [...prev, code]
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsSubmitting(true)

    try {
      // Update user metadata (full_name) if it changed
      const currentFullName = user?.user_metadata?.full_name || ''
      if (formData.full_name.trim() !== currentFullName) {
        try {
          const updatedUser = await authApi.updateUserMetadata({ 
            full_name: formData.full_name.trim() || undefined 
          })
          setUser(updatedUser)
        } catch (metadataError) {
          // Continue with profile update even if metadata update fails
        }
      }

      // Build full URLs from usernames and update user profile
      const updateData: UpdateUserProfileDto = {
        bio: formData.bio || undefined,
        location: formData.location || undefined,
        years_overlanding: formData.years_overlanding || 0,
        instagram_url: buildSocialUrl(formData.instagram_username, 'instagram') || undefined,
        youtube_url: buildSocialUrl(formData.youtube_username, 'youtube') || undefined,
        tiktok_url: buildSocialUrl(formData.tiktok_username, 'tiktok') || undefined,
        facebook_url: buildSocialUrl(formData.facebook_username, 'facebook') || undefined,
        website_url: formData.website_url?.trim() || undefined,
        allergies,
        dietary_restrictions: dietaryRestrictions,
        ...(homeCoords
          ? {
              home_latitude: homeCoords.lat,
              home_longitude: homeCoords.lng,
              home_location_label: homeBaseText || undefined,
            }
          : {})
      }
      
      const updated = await userProfileApi.updateUserProfile(profile.user_id, updateData)
      onProfileUpdated(updated)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update profile')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold">Edit Profile</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
              {error}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="Your full name"
              maxLength={100}
            />
            <p className="text-sm text-gray-500">{formData.full_name?.length || 0}/100 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              placeholder="Tell us about yourself..."
              rows={4}
              maxLength={500}
            />
            <p className="text-sm text-gray-500">{formData.bio?.length || 0}/500 characters</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="City, State/Country"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="years_overlanding">Years Overlanding</Label>
            <Input
              id="years_overlanding"
              type="number"
              min="0"
              max="100"
              value={formData.years_overlanding}
              onChange={(e) => setFormData({ ...formData, years_overlanding: parseInt(e.target.value) || 0 })}
            />
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-1">Home Base</h3>
            <p className="text-sm text-gray-500 mb-4">
              Used to suggest nearby weekend camping trips. Not shown on your public profile.
            </p>
            <div className="space-y-2">
              <Label htmlFor="home_base">Home location</Label>
              <LocationSearchInput
                value={homeBaseText}
                onChange={setHomeBaseText}
                onLocationSelect={handleHomeBaseSelect}
                placeholder="Search your city or address..."
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Social Links</h3>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="instagram_username">Instagram</Label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">instagram.com/</span>
                  <Input
                    id="instagram_username"
                    type="text"
                    value={formData.instagram_username}
                    onChange={(e) => setFormData({ ...formData, instagram_username: e.target.value })}
                    placeholder="username"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">Enter your Instagram username only</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="youtube_username">YouTube</Label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">youtube.com/@</span>
                  <Input
                    id="youtube_username"
                    type="text"
                    value={formData.youtube_username}
                    onChange={(e) => setFormData({ ...formData, youtube_username: e.target.value })}
                    placeholder="channelname"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">Enter your YouTube channel name (without @)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tiktok_username">TikTok</Label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">tiktok.com/@</span>
                  <Input
                    id="tiktok_username"
                    type="text"
                    value={formData.tiktok_username}
                    onChange={(e) => setFormData({ ...formData, tiktok_username: e.target.value })}
                    placeholder="username"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">Enter your TikTok username (without @)</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="facebook_username">Facebook</Label>
                <div className="flex items-center">
                  <span className="text-sm text-gray-500 mr-2">facebook.com/</span>
                  <Input
                    id="facebook_username"
                    type="text"
                    value={formData.facebook_username}
                    onChange={(e) => setFormData({ ...formData, facebook_username: e.target.value })}
                    placeholder="username"
                    className="flex-1"
                  />
                </div>
                <p className="text-xs text-gray-500">Enter your Facebook username or page name</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="website_url">Website</Label>
                <Input
                  id="website_url"
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  placeholder="https://yourwebsite.com"
                />
                <p className="text-xs text-gray-500">Enter full website URL</p>
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-1">Dietary & Allergies</h3>
            <p className="text-sm text-gray-500 mb-4">
              Shared with your crew. Allergies flag meals whose ingredients conflict.
            </p>

            {/* Allergies (free-form) */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <AlertTriangle className="w-4 h-4 text-red-600" />
                Allergies
              </Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={allergyInput}
                  onChange={(e) => setAllergyInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddAllergy()
                    }
                  }}
                  placeholder="Add an allergy (e.g. peanuts)..."
                  className="flex-1"
                />
                <Button type="button" onClick={handleAddAllergy} disabled={!allergyInput.trim()} size="sm">
                  <Plus className="w-4 h-4" />
                  Add
                </Button>
              </div>
              {allergies.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {allergies.map((allergy, index) => (
                    <span
                      key={`allergy-${index}`}
                      className="inline-flex items-center gap-1.5 bg-red-100 text-red-800 border border-red-200 px-2.5 py-1 rounded-full text-sm font-medium"
                    >
                      {allergy}
                      <button
                        type="button"
                        onClick={() => handleRemoveAllergy(index)}
                        className="ml-1 hover:text-red-900 transition-colors"
                        aria-label={`Remove ${allergy}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Dietary restrictions (curated) */}
            <div className="space-y-2 mt-4">
              <Label className="flex items-center gap-1.5">
                <Utensils className="w-4 h-4" />
                Dietary Restrictions
              </Label>
              <div className="flex flex-wrap gap-2">
                {DIET_OPTIONS.map((code) => {
                  const selected = dietaryRestrictions.includes(code)
                  return (
                    <button
                      key={code}
                      type="button"
                      onClick={() => toggleDiet(code)}
                      className={`px-2.5 py-1 rounded-full text-sm font-medium border transition-colors ${
                        selected
                          ? 'bg-adventure-100 text-adventure-800 border-adventure-300'
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {getDietaryRestrictionLabel(code)}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

